import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { ENTER, COMMA } from '@angular/cdk/keycodes';

import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { TestSuite } from '@vendasta/protos/ai_assistants/v1alpha1';
import { TranslateService } from '@ngx-translate/core';
import { AiAssistantI18nModule } from '../../../assets/i18n/ai-assistant-i18n.module';

interface TestSuiteForm {
  name: FormControl<string>;
  description: FormControl<string>;
  tags: FormControl<string[]>;
}

export interface TestSuiteCreateEditModalData {
  testSuite?: TestSuite;
  mode: 'create' | 'edit';
}

export interface TestSuiteCreateEditModalResult {
  name: string;
  description: string;
  tags: string[];
  id?: string;
}

@Component({
  selector: 'ai-test-suite-create-edit-modal',

  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatChipsModule,
    MatIconModule,
    AiAssistantI18nModule,
  ],
  templateUrl: './test-suite-create-edit-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './test-suite-create-edit-modal.component.scss',
})
export class TestSuiteCreateEditModalComponent {
  private readonly dialogRef = inject(MatDialogRef<TestSuiteCreateEditModalComponent, TestSuiteCreateEditModalResult>);
  private readonly translateService = inject(TranslateService);
  protected readonly data = inject<TestSuiteCreateEditModalData>(MAT_DIALOG_DATA, { optional: true });

  form: FormGroup<TestSuiteForm>;
  isEditMode: boolean;
  readonly separatorKeyCodes: number[] = [ENTER, COMMA];

  constructor() {
    this.isEditMode = this.data?.mode === 'edit';
    this.form = new FormGroup<TestSuiteForm>({
      name: new FormControl(this.data?.testSuite?.name || '', {
        nonNullable: true,
        validators: [Validators.required, Validators.minLength(1)],
      }),
      description: new FormControl(this.data?.testSuite?.description || '', {
        nonNullable: true,
      }),
      tags: new FormControl(this.data?.testSuite?.tags ? [...this.data.testSuite.tags] : [], {
        nonNullable: true,
      }),
    });
  }

  get title(): string {
    return this.isEditMode
      ? this.translateService.instant('AI_ASSISTANT.TESTING.SUITE_MODAL.EDIT_TITLE')
      : this.translateService.instant('AI_ASSISTANT.TESTING.SUITE_MODAL.CREATE_TITLE');
  }

  get submitButtonText(): string {
    return this.isEditMode
      ? this.translateService.instant('AI_ASSISTANT.SHARED.SAVE')
      : this.translateService.instant('AI_ASSISTANT.TESTING.SUITE_MODAL.CREATE');
  }

  close(): void {
    this.dialogRef.close();
  }

  save(): void {
    if (this.form.valid) {
      const result: TestSuiteCreateEditModalResult = {
        name: this.form.controls.name.value.trim(),
        description: this.form.controls.description.value.trim(),
        tags: this.form.controls.tags.value.map(t => t.trim()).filter(t => t.length > 0),
        id: this.data?.testSuite?.id,
      };
      this.dialogRef.close(result);
    }
  }

  addTagFromInput(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value && !this.form.controls.tags.value.includes(value)) {
      this.form.controls.tags.setValue([...this.form.controls.tags.value, value]);
    }
    event.chipInput?.clear();
  }

  removeTag(tag: string): void {
    const index = this.form.controls.tags.value.indexOf(tag);
    if (index >= 0) {
      this.form.controls.tags.setValue(this.form.controls.tags.value.filter((_, i) => i !== index));
    }
  }
}
